import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ImageCroppedEvent, ImageCropperComponent, LoadedImage } from 'ngx-image-cropper';
import { Subject, takeUntil, catchError, of } from 'rxjs';
import { PhotoUploadConfig, PhotoUploadModalService } from './photo-upload-modal.service';
import { ToastService } from '../../services/toast.service';

/**
 * Modal genérica de upload/recorte de foto (cropper redondo + zoom).
 *
 * Reutilizada por usuário, cliente e bandeira. A tela que abre define,
 * via `PhotoUploadConfig`, o que fazer com o arquivo recortado (`onConfirm`).
 *
 * Fluxo:
 *  1. Usuário seleciona um arquivo de imagem.
 *  2. O cropper (redondo) permite arrastar e ajustar o zoom.
 *  3. Ao confirmar, o blob recortado é enviado para `config.onConfirm`.
 */
@Component({
  selector: 'app-photo-upload-modal',
  templateUrl: './photo-upload-modal.component.html',
  styleUrl: './photo-upload-modal.component.scss',
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  imports: [CommonModule, ImageCropperComponent]
})
export class PhotoUploadModalComponent implements OnInit, OnDestroy {
  isOpen = false;

  /** Configuração da abertura atual. */
  private config: PhotoUploadConfig | null = null;

  /** Título e rótulo derivados da config (com defaults). */
  title = 'Alterar foto';
  confirmLabel = 'Salvar foto';

  /** Arquivo original selecionado, passado direto ao cropper. */
  imageFile: File | null = null;

  /** Resultado do recorte (blob), pronto para upload. */
  private croppedBlob: Blob | null = null;

  /** Preview do recorte. */
  croppedPreviewUrl: string | null = null;

  // Controle de zoom: 1 = sem zoom. O cropper aplica escala sobre a imagem.
  zoom = 1;
  readonly minZoom = 1;
  readonly maxZoom = 3;
  readonly zoomStep = 0.1;

  isUploading = false;
  loadError: string | null = null;

  // Limites de tamanho/tipo do arquivo de entrada (espelham o backend: 5MB).
  private readonly maxFileSizeBytes = 5 * 1024 * 1024; // 5MB
  private readonly acceptedTypes = ['image/jpeg', 'image/png', 'image/webp'];

  private destroy$ = new Subject<void>();

  constructor(
    private photoModalService: PhotoUploadModalService,
    private toastService: ToastService
  ) {}

  ngOnInit(): void {
    this.photoModalService.open$
      .pipe(takeUntil(this.destroy$))
      .subscribe((config) => {
        this.config = config;
        this.title = config.title ?? 'Alterar foto';
        this.confirmLabel = config.confirmLabel ?? 'Salvar foto';
        this.reset();
        this.isOpen = true;
      });
  }

  /** Disparado pelo <input type="file">. */
  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    if (!this.acceptedTypes.includes(file.type)) {
      this.loadError = 'Formato não suportado. Use JPG, PNG ou WebP.';
      this.imageFile = null;
      return;
    }
    if (file.size > this.maxFileSizeBytes) {
      this.loadError = 'A imagem excede o limite de 5 MB.';
      this.imageFile = null;
      return;
    }

    this.loadError = null;
    this.zoom = 1;
    this.croppedBlob = null;
    this.imageFile = file;
    // Limpa o input para permitir reselecionar o mesmo arquivo depois.
    input.value = '';
  }

  onImageCropped(event: ImageCroppedEvent): void {
    this.croppedBlob = event.blob ?? null;
    if (this.croppedPreviewUrl) {
      URL.revokeObjectURL(this.croppedPreviewUrl);
    }
    this.croppedPreviewUrl = event.objectUrl ?? null;
  }

  onImageLoaded(_image: LoadedImage): void {
    this.loadError = null;
  }

  onLoadImageFailed(): void {
    this.loadError = 'Não foi possível carregar a imagem. Tente outro arquivo.';
    this.imageFile = null;
  }

  zoomIn(): void {
    this.zoom = Math.min(this.maxZoom, +(this.zoom + this.zoomStep).toFixed(2));
  }

  zoomOut(): void {
    this.zoom = Math.max(this.minZoom, +(this.zoom - this.zoomStep).toFixed(2));
  }

  onZoomSlider(value: string): void {
    this.zoom = Number(value);
  }

  /** Permite trocar a imagem sem fechar o modal. */
  clearImage(): void {
    this.imageFile = null;
    this.croppedBlob = null;
    this.zoom = 1;
    if (this.croppedPreviewUrl) {
      URL.revokeObjectURL(this.croppedPreviewUrl);
      this.croppedPreviewUrl = null;
    }
  }

  save(): void {
    if (!this.config) {
      this.toastService.showError('Configuração da foto não encontrada.');
      return;
    }
    if (!this.croppedBlob) {
      this.toastService.showError('Ajuste a imagem antes de salvar.');
      return;
    }

    const file = new File([this.croppedBlob], 'photo.png', { type: 'image/png' });
    const config = this.config;

    this.isUploading = true;
    config.onConfirm(file)
      .pipe(
        takeUntil(this.destroy$),
        catchError((error) => {
          console.error('Erro ao enviar foto:', error);
          let msg = 'Erro ao enviar a imagem.';
          if (error?.error?.messages && Array.isArray(error.error.messages) && error.error.messages.length > 0) {
            msg = error.error.messages.join(' ');
          } else if (typeof error?.error?.message === 'string') {
            msg = error.error.message;
          } else if (error?.status === 0) {
            msg = 'Não foi possível conectar ao servidor. Verifique sua conexão.';
          } else if (error?.status >= 500) {
            msg = 'Erro no servidor. Tente novamente mais tarde.';
          }
          this.isUploading = false;
          this.toastService.showError(msg);
          return of(null);
        })
      )
      .subscribe((response: any) => {
        this.isUploading = false;
        if (response !== null) {
          if (config.successMessage !== null) {
            this.toastService.showSuccess(config.successMessage ?? 'Foto atualizada com sucesso!');
          }
          config.onSuccess?.(response);
          this.close();
        }
      });
  }

  close(): void {
    this.isOpen = false;
    this.reset();
  }

  private reset(): void {
    this.imageFile = null;
    this.croppedBlob = null;
    this.zoom = 1;
    this.isUploading = false;
    this.loadError = null;
    if (this.croppedPreviewUrl) {
      URL.revokeObjectURL(this.croppedPreviewUrl);
      this.croppedPreviewUrl = null;
    }
  }

  ngOnDestroy(): void {
    if (this.croppedPreviewUrl) {
      URL.revokeObjectURL(this.croppedPreviewUrl);
    }
    this.destroy$.next();
    this.destroy$.complete();
  }
}
